import { ExperienceRequirements } from 'next-seo/lib/jsonld/jobPosting';

import { Benefit } from '@pflegia/types/api/benefit/benefit';
import { EmployerNursingApprenticeshipExperience } from '@pflegia/types/api/candidate/nursing-apprenticeship-experience';
import { CERTIFICATE_REQUIREMENTS } from '@pflegia/types/api/employer/cv-certificate';
import { JobPosition, OpeningHours } from '@pflegia/types/api/job-position/job-position';

const minimumMonthsByNursingApprenticeship: Partial<Record<EmployerNursingApprenticeshipExperience, number>> = {
  [EmployerNursingApprenticeshipExperience.BasicNursing]: 6,
  [EmployerNursingApprenticeshipExperience.OneYear]: 12,
  [EmployerNursingApprenticeshipExperience.OneYearTrainingWithLg]: 12,
  [EmployerNursingApprenticeshipExperience.OneYearTrainingOrLg]: 12,
  [EmployerNursingApprenticeshipExperience.OneYearTrainingStrict]: 12,
  [EmployerNursingApprenticeshipExperience.TwoYears]: 24,
};

const getNonEmptyStrings = (values?: string[]) => values?.filter((item: string) => item?.trim()) ?? [];

export interface JobStructuredData {
  qualifications?: string[];
  responsibilities?: string[];
  jobBenefits?: string[];
  experienceRequirements?: ExperienceRequirements;
  workHours?: string;
}

export const useGetJsonldJobStructuredData = (jobPosition: JobPosition): JobStructuredData => {
  const data: JobStructuredData = {};

  const getExperienceRequirementsFromQuestionnaire = (): ExperienceRequirements | undefined => {
    const questionnaire = jobPosition.employer?.questionnaire;
    if (!questionnaire) return undefined;

    const { nursingApprenticeship, certificate } = questionnaire;

    let occupational: ExperienceRequirements['occupational'];
    let educational: ExperienceRequirements['educational'];

    if (nursingApprenticeship) {
      const minimumMonthsOfExperience = minimumMonthsByNursingApprenticeship[nursingApprenticeship];

      if (minimumMonthsOfExperience !== undefined) {
        occupational = {
          '@type': 'OccupationalExperienceRequirements',
          minimumMonthsOfExperience,
        };
      }
    }

    if (certificate && CERTIFICATE_REQUIREMENTS.includes(certificate)) {
      educational = {
        credentialCategory: 'professional certificate',
      };
    }

    if (!occupational && !educational) return undefined;

    return {
      ...(occupational && { occupational }),
      ...(educational && { educational }),
    };
  };

  const qualifications = getNonEmptyStrings(jobPosition.candidateProfile);
  if (qualifications.length > 0) data.qualifications = qualifications;

  const responsibilities = getNonEmptyStrings(jobPosition.responsibilities);
  if (responsibilities.length > 0) data.responsibilities = responsibilities;

  if (jobPosition.benefits && jobPosition.benefits.length > 0) {
    const benefitTitles = getNonEmptyStrings(jobPosition.benefits.map((benefit: Benefit) => benefit.title));
    if (benefitTitles.length > 0) {
      data.jobBenefits = benefitTitles;
    }
  }

  const questionnaireExperienceRequirements = getExperienceRequirementsFromQuestionnaire();
  if (questionnaireExperienceRequirements) data.experienceRequirements = questionnaireExperienceRequirements;

  const workHourTexts: string[] = [];
  if (jobPosition.fullTimeHours) workHourTexts.push(`${jobPosition.fullTimeHours} Stunden/Woche (Vollzeit)`);
  if (jobPosition.partTimeHours) workHourTexts.push(`${jobPosition.partTimeHours} Stunden/Woche (Teilzeit)`);
  if (jobPosition.openingHours && jobPosition.openingHours.length > 0) {
    const hoursText = jobPosition.openingHours
      .map((oh: OpeningHours) => `${oh.dayOfWeek}: ${oh.openingTime} - ${oh.closingTime}`)
      .join('; ');
    if (hoursText.trim()) workHourTexts.push(hoursText);
  }
  if (workHourTexts.length > 0) data.workHours = workHourTexts.join('; ');

  return data;
};
